import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Play, 
  CheckCircle, 
  ArrowRight, 
  Star, 
  Clock, 
  Users, 
  TrendingUp,
  Target,
  Zap,
  Shield,
  ChevronDown,
  ChevronUp,
  Calendar,
  FileText,
  Video,
  MessageCircle,
  Award,
  IndianRupee,
  Sparkles,
  X,
  Timer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { celebrityImages, landscapeVideos, shortsVideos, trustedBrands } from "../data/mock";

// Facebook Pixel tracking helper
const trackFBEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params);
  }
};

// Platform logos (SVG inline for better control)
const PlatformLogos = () => (
  <div className="flex flex-wrap justify-center gap-6 md:gap-10 items-center py-8">
    {/* Meta/Facebook */}
    <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
      <svg viewBox="0 0 36 36" className="w-8 h-8 fill-[#1877F2]">
        <path d="M36 18c0-9.941-8.059-18-18-18S0 8.059 0 18c0 8.984 6.582 16.431 15.188 17.784V23.21h-4.57V18h4.57v-3.969c0-4.512 2.687-7.003 6.797-7.003 1.97 0 4.028.352 4.028.352v4.43h-2.27c-2.235 0-2.93 1.387-2.93 2.81V18h4.992l-.798 5.21h-4.194v12.574C29.418 34.431 36 26.984 36 18"/>
      </svg>
      <span className="text-white font-medium hidden sm:block">Meta</span>
    </div>
    {/* Instagram */}
    <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
      <svg viewBox="0 0 24 24" className="w-8 h-8">
        <defs>
          <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFDC80"/>
            <stop offset="50%" stopColor="#F56040"/>
            <stop offset="100%" stopColor="#C13584"/>
          </linearGradient>
        </defs>
        <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
      <span className="text-white font-medium hidden sm:block">Instagram</span>
    </div>
    {/* YouTube */}
    <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
      <svg viewBox="0 0 24 24" className="w-8 h-8 fill-[#FF0000]">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
      <span className="text-white font-medium hidden sm:block">YouTube</span>
    </div>
    {/* Google */}
    <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
      <svg viewBox="0 0 24 24" className="w-8 h-8">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      <span className="text-white font-medium hidden sm:block">Google</span>
    </div>
  </div>
);

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  useEffect(() => {
    // Get or set the end time in localStorage
    let endTime = localStorage.getItem('gmf_offer_end');
    if (!endTime) {
      // Set 24 hours from now
      endTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem('gmf_offer_end', endTime);
    }

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      const distance = end - now;

      if (distance > 0) {
        setTimeLeft({
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        // Reset timer for another 24 hours
        const newEndTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        localStorage.setItem('gmf_offer_end', newEndTime);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center justify-center gap-2 md:gap-3">
      <Timer className="w-5 h-5 text-[#dc2626] animate-pulse" />
      <span className="text-[#a1a1aa] text-sm">Offer ends in:</span>
      <div className="flex gap-1">
        <div className="bg-[#dc2626] text-white px-2 py-1 rounded font-mono font-bold text-lg">
          {String(timeLeft.hours).padStart(2, '0')}
        </div>
        <span className="text-white font-bold">:</span>
        <div className="bg-[#dc2626] text-white px-2 py-1 rounded font-mono font-bold text-lg">
          {String(timeLeft.minutes).padStart(2, '0')}
        </div>
        <span className="text-white font-bold">:</span>
        <div className="bg-[#dc2626] text-white px-2 py-1 rounded font-mono font-bold text-lg">
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
      </div>
    </div>
  );
};

// FAQ Data
const faqData = [
  {
    question: "Who is this strategy session for?",
    answer: "This session is for business owners, entrepreneurs, coaches, consultants, service providers, freelancers, and anyone who wants to grow their business or personal brand through digital marketing. Whether you're just starting or already running ads — if you lack clarity on what actually works, this is for you."
  },
  {
    question: "What exactly will I get in this 1-hour session?",
    answer: "You'll get a complete, personalized growth roadmap for your business. We'll analyze your current situation, identify gaps, and create a step-by-step action plan covering content strategy, paid ads, funnels, and more. Plus, you'll receive a detailed session report after the call."
  },
  {
    question: "I'm already working with an agency. Will this help?",
    answer: "Absolutely! Most business owners don't know how to evaluate their agency's work. After this session, you'll understand exactly what should be done, what results to expect, and how to hold your agency accountable. You'll finally have clarity."
  },
  {
    question: "What if I'm a complete beginner in digital marketing?",
    answer: "Perfect! This session is designed to give you clarity from zero. We'll explain everything in simple terms and create a roadmap that matches your current level. No jargon, no confusion — just a clear path forward."
  },
  {
    question: "How is this different from free YouTube content?",
    answer: "YouTube gives you generic information. This session gives you a personalized strategy built specifically for YOUR business, YOUR industry, and YOUR goals. Plus, you can ask questions and get real-time answers tailored to your situation."
  },
  {
    question: "What's the money-back guarantee?",
    answer: "If you attend the full session and genuinely feel it wasn't valuable, we'll refund 100% of your money — no questions asked. We're that confident in the value we deliver."
  },
  {
    question: "How do I attend the session?",
    answer: "After booking, you'll receive a confirmation email with a Google Meet link. The session happens online via video call. Make sure you have a stable internet connection and a quiet place to focus."
  },
  {
    question: "Can I reschedule if something comes up?",
    answer: "Yes! You can reschedule your session up to 24 hours before the scheduled time. Just reply to your confirmation email or contact us on WhatsApp."
  }
];

// What You'll Learn Data
const learningPoints = [
  { icon: Target, title: "Complete Social Media Blueprint", desc: "The exact framework we use for 500+ successful projects" },
  { icon: TrendingUp, title: "Meta Ads Mastery", desc: "How Meta ads actually work — targeting, budgets, scaling" },
  { icon: Sparkles, title: "Content Strategy Secrets", desc: "What big brands do differently in content & design" },
  { icon: Video, title: "Video Production Formula", desc: "Scripting, hooks, production & post-production" },
  { icon: Zap, title: "AI in Marketing", desc: "Leverage AI tools to 10x your marketing output" },
  { icon: IndianRupee, title: "Marketing Finance", desc: "How much to spend to achieve X ROI" },
];

// Combined Testimonial Videos (from props + new ones)
const testimonialVideos = [
  // New testimonials from user
  { id: "yZT7SGTEdpc", title: "Client Success Story", type: "landscape" },
  { id: "URaRNsXaXng", title: "Business Growth Testimonial", type: "landscape" },
  { id: "bluSkA_eVjM", title: "Client Transformation", type: "landscape" },
  { id: "H62HSpuMxIE", title: "Quick Review", type: "shorts" },
  { id: "vNzkvy1f_DE", title: "Client Feedback", type: "shorts" },
  // From homepage landscapeVideos
  { id: "TYtrE9tCuuA", title: "PLeathers - Footwear Brand", type: "landscape" },
  { id: "cLqbxHR31fQ", title: "Anand Krishi Khamar", type: "landscape" },
  { id: "4OSdjAJ8hHM", title: "Dolls Heaven - Travel Agency", type: "landscape" },
  // From homepage shortsVideos
  { id: "VeguCX70Woc", title: "Ahana Easy English", type: "shorts" },
  { id: "CS_jVsaxKT8", title: "Astrologer Prasenjit Sastri", type: "shorts" },
  { id: "gSJ7gr_DMPw", title: "Kidzee", type: "shorts" },
  { id: "A1DqCDNyfYM", title: "Anushree Malhotra", type: "shorts" },
  { id: "locCycmAqMA", title: "Kritanjali Boutique", type: "shorts" },
];

// Before vs After Data
const beforeAfterData = [
  { before: "Confused about what works", after: "Crystal clear roadmap" },
  { before: "Wasting money on random ads", after: "Strategic ad spending with ROI" },
  { before: "Copying competitors blindly", after: "Unique strategy for your brand" },
  { before: "Dependent on agencies", after: "In control of your growth" },
  { before: "No measurable results", after: "Clear KPIs and tracking" },
  { before: "Overwhelmed by options", after: "Focused action plan" },
];

const BOOKING_URL = "https://cubemultimedia.dayschedule.com/11-marketing-strategy-call-with-senior-consultant";

export default function GMFLandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [playingTestimonial, setPlayingTestimonial] = useState(null);

  // Scroll to top on mount and track ViewContent
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track ViewContent event for GMF landing page
    trackFBEvent('ViewContent', {
      content_name: 'GMF Strategy Call Landing Page',
      content_category: 'Strategy Call',
      value: 299,
      currency: 'INR'
    });
  }, []);

  const handleBookNow = () => {
    // Track Schedule event when user clicks Book Now
    trackFBEvent('Schedule', {
      content_name: 'GMF 1:1 Strategy Call',
      value: 299,
      currency: 'INR'
    });
    
    // Track Lead event as well
    trackFBEvent('Lead', {
      content_name: 'GMF Strategy Call Booking',
      content_category: 'Strategy Call',
      value: 299,
      currency: 'INR'
    });
    
    window.open(BOOKING_URL, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Floating CTA for Mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent lg:hidden">
        <Button 
          onClick={handleBookNow}
          className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-4 rounded-xl text-lg font-bold shadow-lg shadow-red-500/20"
        >
          Book Your Strategy Call — ₹299 <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>

      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-8 pb-24 lg:pb-8 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#dc2626]/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#dc2626]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-[#dc2626]/5 to-transparent rounded-full" />
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-full px-4 py-2 mb-6"
          >
            <Sparkles className="w-4 h-4 text-[#dc2626]" />
            <span className="text-sm text-[#dc2626] font-medium">Limited Period Offer — 88% OFF</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Stop Guessing. Start Growing.
            <br />
            <span className="text-[#dc2626]">Get Your Personalized Digital Growth Roadmap</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-[#a1a1aa] max-w-3xl mx-auto mb-6"
          >
            In just 1 hour, discover the exact formula that has generated <span className="text-white font-semibold">₹15 Crore+ in revenue</span> for our clients. No fluff. No generic advice. Just a clear, actionable roadmap built for YOUR business.
          </motion.p>

          {/* Platform Logos */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-6"
          >
            <p className="text-[#71717a] text-sm mb-2">Master marketing on</p>
            <PlatformLogos />
          </motion.div>

          {/* Video Player */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative max-w-3xl mx-auto mb-8 rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-red-500/10"
          >
            {!isVideoPlaying ? (
              <div 
                className="relative aspect-video bg-black cursor-pointer group"
                onClick={() => setIsVideoPlaying(true)}
              >
                <img 
                  src={`https://img.youtube.com/vi/zscjfLoqbQg/maxresdefault.jpg`}
                  alt="Watch the video"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#dc2626] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-red-500/30">
                    <Play className="w-8 h-8 text-white fill-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-left">
                  <p className="text-white font-medium text-sm md:text-base">Watch: What is GMF Strategy Call?</p>
                </div>
              </div>
            ) : (
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/zscjfLoqbQg?autoplay=1&rel=0&loop=1&playlist=zscjfLoqbQg&modestbranding=1"
                  title="GMF Strategy Call"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </motion.div>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mb-6"
          >
            <CountdownTimer />
          </motion.div>

          {/* Price & CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="flex items-center gap-3">
              <span className="text-[#71717a] line-through text-xl">₹2,500</span>
              <span className="text-4xl md:text-5xl font-bold text-white">₹299</span>
              <span className="bg-[#dc2626] text-white text-xs font-bold px-2 py-1 rounded animate-pulse">88% OFF</span>
            </div>
            <Button 
              onClick={handleBookNow}
              className="hidden lg:flex bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 rounded-xl text-lg font-bold shadow-lg shadow-red-500/20 items-center gap-2 hover:scale-105 transition-transform"
            >
              Book Your Strategy Call Now <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-[#71717a] flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-500" />
              100% Money-Back Guarantee — Zero Risk
            </p>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-[#71717a]"
          >
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Users className="w-4 h-4 text-[#dc2626]" />
              <span>500+ Projects</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <TrendingUp className="w-4 h-4 text-[#dc2626]" />
              <span>₹15Cr+ Revenue</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Clock className="w-4 h-4 text-[#dc2626]" />
              <span>6+ Years</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== VIDEO TESTIMONIALS ===== */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Real Results. <span className="text-[#dc2626]">Real Stories.</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg">
              Hear from business owners who transformed their growth
            </p>
          </motion.div>

          {/* Landscape Videos */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {testimonialVideos.filter(v => v.type === 'landscape').slice(0, 6).map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative rounded-xl overflow-hidden border border-white/[0.06] aspect-video group"
              >
                {playingTestimonial !== video.id ? (
                  <div 
                    className="relative w-full h-full bg-black cursor-pointer"
                    onClick={() => setPlayingTestimonial(video.id)}
                  >
                    <img 
                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#dc2626] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-white fill-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white text-sm font-medium truncate">{video.title}</p>
                    </div>
                  </div>
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&loop=1&playlist=${video.id}&modestbranding=1`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Shorts Videos */}
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
            {testimonialVideos.filter(v => v.type === 'shorts').map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="relative rounded-xl overflow-hidden border border-white/[0.06] aspect-[9/16] w-[180px] md:w-[200px] shrink-0 snap-center group"
              >
                {playingTestimonial !== video.id ? (
                  <div 
                    className="relative w-full h-full bg-black cursor-pointer"
                    onClick={() => setPlayingTestimonial(video.id)}
                  >
                    <img 
                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white text-xs font-medium truncate">{video.title}</p>
                    </div>
                  </div>
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&loop=1&playlist=${video.id}&modestbranding=1`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CELEBRITY PORTFOLIO ===== */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Trusted by <span className="text-[#dc2626]">Celebrities & Brands</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
            {celebrityImages.slice(0, 12).map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="relative aspect-square rounded-xl overflow-hidden group"
              >
                <img
                  src={image.src}
                  alt={`Celebrity ${image.id}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUSTED BRANDS ===== */}
      <section className="py-10 md:py-12 overflow-hidden border-y border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Trusted by <span className="text-[#dc2626]">India's Top Brands</span>
            </h2>
          </motion.div>
          
          {/* Scrolling Brand Logos */}
          <div className="relative">
            <div className="flex gap-8 md:gap-12 animate-scroll">
              {[...trustedBrands, ...trustedBrands].map((brand, i) => (
                <div 
                  key={`${brand.name}-${i}`} 
                  className={`shrink-0 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity duration-300 ${brand.size === 'large' ? 'h-14 w-28' : 'h-10 w-20'}`}
                >
                  <img 
                    src={brand.logo} 
                    alt={brand.name} 
                    className="max-h-full max-w-full object-contain brightness-0 invert" 
                    loading="lazy" 
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLATFORM SECTION ===== */}
      <section className="py-12 border-y border-white/[0.06] bg-gradient-to-r from-[#1877F2]/5 via-[#E4405F]/5 to-[#FF0000]/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#a1a1aa] mb-4">Learn to master ads & content on</p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center">
            {/* Meta */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-2xl bg-[#1877F2]/10 flex items-center justify-center">
                <svg viewBox="0 0 36 36" className="w-10 h-10 fill-[#1877F2]">
                  <path d="M36 18c0-9.941-8.059-18-18-18S0 8.059 0 18c0 8.984 6.582 16.431 15.188 17.784V23.21h-4.57V18h4.57v-3.969c0-4.512 2.687-7.003 6.797-7.003 1.97 0 4.028.352 4.028.352v4.43h-2.27c-2.235 0-2.93 1.387-2.93 2.81V18h4.992l-.798 5.21h-4.194v12.574C29.418 34.431 36 26.984 36 18"/>
                </svg>
              </div>
              <span className="text-white font-medium">Facebook Ads</span>
            </div>
            {/* Instagram */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFDC80]/10 via-[#F56040]/10 to-[#C13584]/10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-10 h-10">
                  <defs>
                    <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FFDC80"/>
                      <stop offset="50%" stopColor="#F56040"/>
                      <stop offset="100%" stopColor="#C13584"/>
                    </linearGradient>
                  </defs>
                  <path fill="url(#ig-grad)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <span className="text-white font-medium">Instagram</span>
            </div>
            {/* YouTube */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-10 h-10 fill-[#FF0000]">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <span className="text-white font-medium">YouTube</span>
            </div>
            {/* Google */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-10 h-10">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span className="text-white font-medium">Google Ads</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU'LL LEARN ===== */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What You'll <span className="text-[#dc2626]">Learn</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
              Everything you need to know to grow your business digitally — in just 1 hour
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {learningPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 hover:border-[#dc2626]/30 hover:bg-white/[0.04] transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#dc2626]/10 flex items-center justify-center mb-4 group-hover:bg-[#dc2626]/20 transition-colors">
                  <point.icon className="w-6 h-6 text-[#dc2626]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-[#71717a] text-sm">{point.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU'LL GET ===== */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What You'll <span className="text-[#dc2626]">Get</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              { icon: Video, title: "1-Hour Live Strategy Session", desc: "Personal 1:1 video call with Sumanta Dey" },
              { icon: FileText, title: "Detailed Session Report", desc: "Written roadmap with step-by-step action items" },
              { icon: Target, title: "Customized Growth Framework", desc: "GMF framework tailored to your industry" },
              { icon: MessageCircle, title: "Q&A Session", desc: "Get all your doubts cleared in real-time" },
              { icon: Calendar, title: "30-Day Action Plan", desc: "Know exactly what to do for the next 30 days" },
              { icon: Shield, title: "100% Money-Back Guarantee", desc: "Full refund if you don't find value" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 hover:border-[#dc2626]/20 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#dc2626]/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-6 h-6 text-[#dc2626]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-[#71717a]">{item.desc}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
              </motion.div>
            ))}
          </div>

          {/* Total Value */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 bg-gradient-to-r from-[#dc2626]/20 to-[#dc2626]/5 border border-[#dc2626]/30 rounded-2xl p-6 md:p-8 text-center"
          >
            <CountdownTimer />
            <p className="text-[#a1a1aa] mb-2 mt-4">Total Value</p>
            <p className="text-3xl md:text-4xl font-bold text-white line-through opacity-50 mb-1">₹2,500+</p>
            <p className="text-xl text-[#a1a1aa] mb-4">Today's Price</p>
            <p className="text-5xl md:text-6xl font-bold text-[#dc2626]">₹299</p>
            <Button 
              onClick={handleBookNow}
              className="mt-6 bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-4 rounded-xl text-lg font-bold hover:scale-105 transition-transform"
            >
              Book Now & Save ₹2,000+ <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ===== BEFORE VS AFTER ===== */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              You — <span className="text-[#dc2626]">Before</span> vs <span className="text-green-500">After</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/[0.02] border border-[#dc2626]/20 rounded-2xl p-6"
            >
              <h3 className="text-xl font-bold text-[#dc2626] mb-6 flex items-center gap-2">
                <X className="w-6 h-6" /> Before the Session
              </h3>
              <ul className="space-y-4">
                {beforeAfterData.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#a1a1aa]">
                    <div className="w-2 h-2 rounded-full bg-[#dc2626]" />
                    {item.before}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* After */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-green-500/5 border border-green-500/20 rounded-2xl p-6"
            >
              <h3 className="text-xl font-bold text-green-500 mb-6 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" /> After the Session
              </h3>
              <ul className="space-y-4">
                {beforeAfterData.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    {item.after}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT SUMANTA ===== */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Meet Your <span className="text-[#dc2626]">Strategist</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-6 md:p-10"
          >
            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
              {/* Photo */}
              <div className="shrink-0">
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden border-4 border-[#dc2626]/30 shadow-lg shadow-red-500/10">
                  <img
                    src="https://res.cloudinary.com/drcoefnzk/image/upload/v1771793861/370608373_762029959267707_3978280535694695982_n_gqi2hc.jpg"
                    alt="Sumanta Dey"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">Sumanta Dey</h3>
                <p className="text-[#dc2626] font-medium mb-4">Founder & CEO, Cube Multimedia</p>
                
                {/* Stats */}
                <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
                  <div className="bg-[#dc2626]/10 rounded-lg px-4 py-2">
                    <p className="text-[#dc2626] font-bold">₹15Cr+</p>
                    <p className="text-xs text-[#71717a]">Revenue Generated</p>
                  </div>
                  <div className="bg-[#dc2626]/10 rounded-lg px-4 py-2">
                    <p className="text-[#dc2626] font-bold">6+ Years</p>
                    <p className="text-xs text-[#71717a]">Experience</p>
                  </div>
                  <div className="bg-[#dc2626]/10 rounded-lg px-4 py-2">
                    <p className="text-[#dc2626] font-bold">500+</p>
                    <p className="text-xs text-[#71717a]">Projects</p>
                  </div>
                </div>

                <p className="text-[#a1a1aa] leading-relaxed mb-4">
                  Hi, I'm Sumanta Dey — a Digital Marketing Strategist based in Kolkata. Over the last 6 years, I've helped 500+ businesses build their digital presence and generate real results.
                </p>
                <p className="text-[#a1a1aa] leading-relaxed mb-4">
                  Most businesses don't fail because of bad products. <span className="text-white font-medium">They fail because they never had a clear roadmap.</span>
                </p>
                <p className="text-white font-medium">
                  That's exactly what this 1-hour GMF Strategy Call is — your personalized growth roadmap.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Frequently Asked <span className="text-[#dc2626]">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.1] transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#dc2626] shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#71717a] shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-5 pb-5 text-[#a1a1aa]"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 md:py-24 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#dc2626]/10 to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#dc2626]/20 rounded-full blur-[100px]" />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-500 font-medium">100% Money-Back Guarantee</span>
            </div>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to Get Your <span className="text-[#dc2626]">Growth Roadmap?</span>
            </h2>
            
            <p className="text-[#a1a1aa] text-lg mb-6 max-w-xl mx-auto">
              Stop guessing. Stop wasting money. Get clarity on exactly what you need to do to grow your business digitally.
            </p>

            <CountdownTimer />

            <div className="flex flex-col items-center gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[#71717a] line-through text-xl">₹2,500</span>
                <span className="text-4xl md:text-5xl font-bold text-white">₹299</span>
              </div>
              
              <Button 
                onClick={handleBookNow}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-10 py-6 rounded-xl text-xl font-bold shadow-lg shadow-red-500/20 hover:scale-105 transition-transform"
              >
                Book Your Strategy Call Now <ArrowRight className="ml-2 w-6 h-6" />
              </Button>

              <p className="text-sm text-[#71717a] mt-2">
                Limited slots available. Price increases soon.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#52525b] text-sm">
            © {new Date().getFullYear()} Cube Multimedia. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Bottom padding for mobile CTA */}
      <div className="h-24 lg:hidden" />
    </div>
  );
}
